import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/utils/api';
import { Video, Sparkles, Loader2, Play, Trash2, AlertTriangle, CheckCircle2, Clock, ExternalLink, Zap } from 'lucide-react';
import Layout from '@/components/Layout';

export default function VideoStudio() {
  const { i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [form, setForm] = useState({ topic: '', audience: 'owner', duration_seconds: 120, tone: 'professional', script: '' });
  const [error, setError] = useState('');
  const [active, setActive] = useState(null);

  useEffect(() => { fetchVideos(); }, []);

  const fetchVideos = async () => {
    setLoading(true);
    try { const r = await api.listVideos(); setVideos(r.data); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  // Poll status of any active/processing/pending videos
  useEffect(() => {
    const inProgress = videos.filter(v => ['pending', 'processing', 'submitted'].includes(v.status));
    if (inProgress.length === 0) return;
    const t = setInterval(async () => {
      for (const v of inProgress) {
        try {
          const r = await api.getVideoStatus(v.id);
          setVideos(prev => prev.map(p => p.id === v.id ? { ...p, ...r.data } : p));
        } catch { /* silent */ }
      }
    }, 5000);
    return () => clearInterval(t);
  }, [videos]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.topic) { setError(isAr ? 'الموضوع مطلوب' : 'Topic is required'); return; }
    setGenerating(true);
    try {
      const r = await api.generateVideo(form);
      setVideos(prev => [r.data, ...prev]);
      setForm({ topic: '', audience: 'owner', duration_seconds: 120, tone: 'professional', script: '' });
    } catch (e) {
      setError(e?.response?.data?.detail || (isAr ? 'فشل التوليد' : 'Generation failed'));
    } finally { setGenerating(false); }
  };

  const removeVideo = async (id) => {
    if (!window.confirm(isAr ? 'حذف الفيديو؟' : 'Delete video?')) return;
    try { await api.deleteVideo(id); fetchVideos(); if (active?.id === id) setActive(null); } catch (e) { console.error(e); }
  };

  const STATUS_META = {
    mock: { color: 'bg-amber-100 text-amber-800', icon: AlertTriangle, label: isAr ? 'نسخة تجريبية (Mock)' : 'Mock mode' },
    pending: { color: 'bg-blue-100 text-blue-700', icon: Clock, label: isAr ? 'في الانتظار' : 'Pending' },
    submitted: { color: 'bg-blue-100 text-blue-700', icon: Clock, label: isAr ? 'مُرسل' : 'Submitted' },
    processing: { color: 'bg-violet-100 text-violet-700', icon: Loader2, label: isAr ? 'جاري المعالجة' : 'Processing' },
    completed: { color: 'bg-green-100 text-green-700', icon: CheckCircle2, label: isAr ? 'مكتمل' : 'Completed' },
    mock_completed: { color: 'bg-green-100 text-green-700', icon: CheckCircle2, label: isAr ? 'مكتمل (Mock)' : 'Completed (Mock)' },
    error: { color: 'bg-red-100 text-red-700', icon: AlertTriangle, label: isAr ? 'خطأ' : 'Error' },
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center"><Video className="h-5 w-5 text-violet-600" strokeWidth={1.5} /></div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-gray-900">{isAr ? 'استوديو الفيديو AI' : 'AI Video Studio'}</h1>
              <p className="text-sm text-gray-500">{isAr ? 'فيديوهات بصوت عربي + ترجمة إنجليزية لتسويق REVA' : 'Create marketing videos with Arabic voice + English subtitles powered by HeyGen'}</p>
            </div>
          </div>
        </div>

        {error && <div className="text-sm text-red-700 bg-red-50 border border-red-100 px-4 py-2.5 rounded-xl">{error}</div>}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Generation form */}
          <Card className="shadow-soft border-gray-100 lg:col-span-1">
            <CardHeader><CardTitle className="font-heading text-base flex items-center gap-2"><Sparkles className="h-4 w-4 text-violet-600" /> {isAr ? 'إنشاء فيديو جديد' : 'New video'}</CardTitle></CardHeader>
            <CardContent>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label className="text-xs text-gray-600 font-medium">{isAr ? 'الموضوع' : 'Topic'}*</Label>
                  <Input value={form.topic} onChange={e => setForm({ ...form, topic: e.target.value })} placeholder={isAr ? 'مثال: كيف تزيد إيرادات عقارك بـ 35%' : 'e.g. How to grow rental revenue by 35%'} data-testid="video-topic" />
                </div>
                <div>
                  <Label className="text-xs text-gray-600 font-medium">{isAr ? 'الجمهور' : 'Audience'}</Label>
                  <Select value={form.audience} onValueChange={v => setForm({ ...form, audience: v })}>
                    <SelectTrigger data-testid="video-audience"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="owner">{isAr ? 'مالك عقار' : 'Property Owner'}</SelectItem>
                      <SelectItem value="pmc">{isAr ? 'شركة إدارة عقارات' : 'Property Management Co.'}</SelectItem>
                      <SelectItem value="investor">{isAr ? 'مستثمر' : 'Investor'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-gray-600 font-medium">{isAr ? 'النبرة' : 'Tone'}</Label>
                    <Select value={form.tone} onValueChange={v => setForm({ ...form, tone: v })}>
                      <SelectTrigger data-testid="video-tone"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="professional">{isAr ? 'احترافي' : 'Professional'}</SelectItem>
                        <SelectItem value="persuasive">{isAr ? 'مقنع' : 'Persuasive'}</SelectItem>
                        <SelectItem value="enterprise">{isAr ? 'مؤسسي' : 'Enterprise'}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600 font-medium">{isAr ? 'المدة (ثانية)' : 'Duration (s)'}</Label>
                    <Input type="number" min={60} max={180} value={form.duration_seconds} onChange={e => setForm({ ...form, duration_seconds: parseInt(e.target.value) })} data-testid="video-duration" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-gray-600 font-medium">{isAr ? 'سيناريو (اختياري)' : 'Script (optional)'}</Label>
                  <textarea rows={3} value={form.script} onChange={e => setForm({ ...form, script: e.target.value })} placeholder={isAr ? 'سنُولّد سيناريو احترافي إذا تركته فارغاً' : "We'll auto-generate a script if you leave this empty"} className="w-full mt-1 px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-primary resize-none" data-testid="video-script" />
                </div>
                <div className="p-3 bg-violet-50 border border-violet-100 rounded-xl text-xs text-violet-900">
                  <div className="flex items-center gap-1.5 font-bold mb-1"><Zap className="h-3 w-3" /> {isAr ? 'ميزات مدمجة' : 'Built-in features'}</div>
                  <ul className="space-y-0.5 ms-4 list-disc">
                    <li>{isAr ? 'صوت عربي طبيعي' : 'Natural Arabic voiceover'}</li>
                    <li>{isAr ? 'ترجمة إنجليزية متزامنة' : 'English subtitles auto-synced'}</li>
                    <li>{isAr ? 'مشاهد REVA + ألوان العلامة' : 'REVA scenes + brand colors'}</li>
                    <li>{isAr ? 'بيانات سوق سعودية حية (SREM)' : 'Live Saudi market data (SREM)'}</li>
                  </ul>
                </div>
                <Button type="submit" disabled={generating} className="w-full gradient-primary text-white rounded-xl" data-testid="video-submit">
                  {generating ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : <Sparkles className="h-4 w-4 me-2" />}
                  {isAr ? 'توليد الفيديو' : 'Generate video'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Library */}
          <Card className="shadow-soft border-gray-100 lg:col-span-2">
            <CardHeader><CardTitle className="font-heading text-base">{isAr ? 'مكتبتي' : 'My library'} ({videos.length})</CardTitle></CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-10 text-gray-500"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
              ) : videos.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-14 h-14 bg-violet-50 rounded-2xl flex items-center justify-center mx-auto mb-3">
                    <Video className="h-7 w-7 text-violet-400" strokeWidth={1.5} />
                  </div>
                  <p className="text-sm text-gray-500">{isAr ? 'لا توجد فيديوهات بعد' : 'No videos yet'}</p>
                </div>
              ) : (
                <div className="space-y-2" data-testid="video-list">
                  {videos.map(v => {
                    const meta = STATUS_META[v.status] || STATUS_META.pending;
                    const Icon = meta.icon;
                    return (
                      <div key={v.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl gap-3" data-testid={`video-row-${v.id}`}>
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                            <Video className="h-4 w-4 text-violet-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className="text-sm font-semibold text-gray-900 truncate">{v.topic}</span>
                              <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${meta.color} flex items-center gap-1`}>
                                <Icon className={`h-3 w-3 ${v.status === 'processing' ? 'animate-spin' : ''}`} /> {meta.label}
                              </span>
                            </div>
                            <div className="text-xs text-gray-500">{v.audience} · {v.duration_seconds}s · {new Date(v.created_at).toLocaleDateString()}</div>
                            {(v.status === 'processing' || v.status === 'pending') && v.progress_pct !== undefined && (
                              <div className="w-full h-1 bg-gray-200 rounded-full mt-1.5 overflow-hidden">
                                <div className="h-1 bg-violet-500 transition-all" style={{ width: `${v.progress_pct}%` }} />
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {v.video_url && (
                            <a href={v.video_url} target="_blank" rel="noreferrer" className="p-2 text-gray-500 hover:text-primary"><ExternalLink className="h-4 w-4" /></a>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => setActive(v)} data-testid={`video-view-${v.id}`}>{isAr ? 'تفاصيل' : 'Details'}</Button>
                          <Button variant="ghost" size="sm" onClick={() => removeVideo(v.id)} data-testid={`video-delete-${v.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Detail drawer */}
        {active && (
          <Card className="shadow-glass border-gray-100">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-base">{active.topic}</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setActive(null)}>×</Button>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                <div className="p-2 bg-gray-50 rounded-lg"><div className="text-[10px] uppercase text-gray-400">Audience</div><div className="text-sm font-semibold">{active.audience}</div></div>
                <div className="p-2 bg-gray-50 rounded-lg"><div className="text-[10px] uppercase text-gray-400">Duration</div><div className="text-sm font-semibold">{active.duration_seconds}s</div></div>
                <div className="p-2 bg-gray-50 rounded-lg"><div className="text-[10px] uppercase text-gray-400">Voice</div><div className="text-sm font-semibold">Arabic</div></div>
                <div className="p-2 bg-gray-50 rounded-lg"><div className="text-[10px] uppercase text-gray-400">Subtitles</div><div className="text-sm font-semibold">English</div></div>
              </div>
              {active.is_mock && (
                <div className="p-3 bg-amber-50 border border-amber-100 rounded-xl mb-3 text-xs text-amber-900">
                  <b>{isAr ? 'وضع المحاكاة' : 'Mock mode'}:</b> {isAr ? 'لم يتم ضبط HEYGEN_API_KEY في .env. يتم حفظ الموجه (prompt) وتشغيل الواجهة بالكامل. أضف المفتاح لتفعيل العرض الحقيقي.' : 'HEYGEN_API_KEY is not set in backend/.env. The full UI works and the prompt is saved. Add the key to enable real rendering.'}
                </div>
              )}
              <div>
                <div className="text-[10px] uppercase font-bold text-gray-500 tracking-wider mb-1">Generated prompt</div>
                <pre className="text-xs whitespace-pre-wrap bg-gray-50 rounded-xl p-3 max-h-72 overflow-y-auto leading-relaxed">{active.prompt}</pre>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
